import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { usePortalAuth } from '../../hooks/usePortalAuth'
import { useEnrollmentsForPortalUser } from '../../hooks/useEnrollmentsForPortalUser'
import { usePortalQueryClientId } from '../../hooks/portalHostQueryGate'
import { getWalkInReceiptItems } from '../../../../pure-js/libs/getWalkInReceiptItems'
import type { WalkInReceiptItem } from '../../../../pure-js/libs/getWalkInReceiptItems'
import { buildRoute } from '../../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../../pure-js/libs/RoutePath'
import { getClient } from '../../libs/DBApiHandler'
import { Client } from '../../../../pure-js/types/GrooverTypes'
import useAppState from '../../hooks/useAppState'
import { getGrooverText } from '../../libs/GrooverTextRepository'
import { PortalWalkinTicketCard } from './PortalWalkinTicketCard'
import { PortalWalkinReceiptCard } from './PortalWalkinReceiptCard'
import {
  DetailOverlay,
  EmptyDescription,
  EmptyIcon,
  EmptyState,
  EmptyTitle,
  ErrorContainer,
  ErrorIcon,
  ErrorText,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  MainContent,
  PageContainer,
  PageTitle,
  ReceiptList,
  RetryButton
} from './PortalWalkinTicketsPage.styles'

export const PortalWalkinTicketsPage: React.FC = () => {
  const { portalUser, portalClaimsReady } = usePortalAuth()
  const { state } = useAppState()
  const navigate = useNavigate()
  const { paymentRef } = useParams<{ paymentRef?: string }>()
  const selectedLanguage = state.selectedLanguage
  const getText = useCallback((key: string) => getGrooverText(key, selectedLanguage), [selectedLanguage])
  const [selectedItem, setSelectedItem] = useState<WalkInReceiptItem | null>(null)
  const [clients, setClients] = useState<Record<string, Client | null>>({})

  const enrollmentUserId = portalClaimsReady ? (portalUser?.enrollmentUserId ?? null) : null
  const clientId = usePortalQueryClientId()
  const { data: enrollments, isLoading, error } = useEnrollmentsForPortalUser(enrollmentUserId, clientId)

  const receiptItems = useMemo(() => {
    const items = getWalkInReceiptItems(enrollments)
    return [...items].sort((a, b) => {
      const dateA = a.payment.paymentDate ? new Date(a.payment.paymentDate).getTime() : 0
      const dateB = b.payment.paymentDate ? new Date(b.payment.paymentDate).getTime() : 0
      return dateB - dateA
    })
  }, [enrollments])

  // Fetch client data for each distinct clientId
  useEffect(() => {
    const clientIds = [...new Set(receiptItems.map((i) => i.enrollment.clientId).filter(Boolean))]
    for (const cid of clientIds) {
      if (cid in clients) continue
      getClient(cid).then((c) => {
        setClients((prev) => ({ ...prev, [cid]: c ?? null }))
      })
    }
  }, [receiptItems])

  // Deep-link: open receipt from URL param
  useEffect(() => {
    if (!paymentRef || !receiptItems.length) return
    const match = receiptItems.find((i) => i.payment.paymentRef === paymentRef)
    if (match) {
      setSelectedItem(match)
    } else {
      // FR4.3: paymentRef matches none of the user's items — silently fall back to list
      navigate(buildRoute(RoutePath.PORTAL_WALKIN_TICKETS), { replace: true })
    }
  }, [paymentRef, receiptItems, navigate])

  const openReceipt = useCallback(
    (item: WalkInReceiptItem) => {
      setSelectedItem(item)
      navigate(
        buildRoute(RoutePath.PORTAL_WALKIN_TICKET_DETAIL, { paymentRef: item.payment.paymentRef ?? item.enrollment.id })
      )
    },
    [navigate]
  )

  const closeReceipt = useCallback(() => {
    setSelectedItem(null)
    navigate(buildRoute(RoutePath.PORTAL_WALKIN_TICKETS))
  }, [navigate])

  useEffect(() => {
    if (!selectedItem) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeReceipt()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [selectedItem, closeReceipt])

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>{getText('portalWalkinLoading')}</LoadingText>
        </LoadingContainer>
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorText>{getText('portalWalkinLoadError')}</ErrorText>
          <RetryButton onClick={() => window.location.reload()}>{getText('portalTryAgain')}</RetryButton>
        </ErrorContainer>
      </PageContainer>
    )
  }

  if (receiptItems.length === 0) {
    return (
      <PageContainer>
        <MainContent>
          <PageTitle>{getText('portalYourWalkinTickets')}</PageTitle>
          <EmptyState>
            <EmptyIcon>🧾</EmptyIcon>
            <EmptyTitle>{getText('portalNoWalkinTicketsTitle')}</EmptyTitle>
            <EmptyDescription>{getText('portalNoWalkinTickets')}</EmptyDescription>
          </EmptyState>
        </MainContent>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <MainContent>
        <PageTitle>{getText('portalYourWalkinTickets')}</PageTitle>
        <ReceiptList>
          {receiptItems.map((item) => (
            <PortalWalkinTicketCard
              key={`${item.enrollment.id}-${item.payment.paymentRef ?? item.payment.paymentDate}`}
              item={item}
              client={clients[item.enrollment.clientId] ?? null}
              getText={getText}
              onClick={() => openReceipt(item)}
            />
          ))}
        </ReceiptList>
      </MainContent>

      {selectedItem && enrollmentUserId && (
        <PortalWalkinReceiptCard
          item={selectedItem}
          client={clients[selectedItem.enrollment.clientId] ?? null}
          enrollmentUserId={enrollmentUserId}
          getText={getText}
          onClose={closeReceipt}
        />
      )}
    </PageContainer>
  )
}
